<template>
  <div class="section">
    <div v-if="!isLoading">
      <div class="_flex-row _flex:justify(space-between) _p:top(4)">
        <h2 class="h3 _p:left(4) _p:right(2)">{{ currentSection.label }}</h2>
        <div class="_is-hidden:md _is-hidden:lg _is-hidden:xl _is-hidden:xxl _text:sm _text-grey-900  _p:right(4) _p:left(2) no-wrap">
          {{ $t('shared.step') }} {{ activeSectionIndex + 1 }} {{ $t('shared.of') }} {{ sections.length }}
        </div>
      </div>

      <ds ds="form"
        v-if="currentSectionHasElements"
        class="section__elements"
        id="sectionForm"
        @valid="e => isValid = e.detail"
        @submit="submitSection"
      >
        <div class="_p:top(2) _p:bottom(4) _p:x(4)">
          <div :class="`section__elements--${element.type} _m:bottom(4)`" v-for="(element) in elements" :key="element.id">
            <div v-if="['fieldset', 'fieldset-files', 'fieldset-grouped'].includes(element.type)">
              <FieldsetElement
                :element="element"
                :elementValues="elementValues"
                :isGrouped="element.type==='fieldset-grouped'"
                :isReadonly="isReadonly"
              >
                <div
                  :class="`section__elements--${element.layout} ${element.layout === 'default' ? '_col(12) _col:md(6) ' : ''} `"
                  v-for="(field) in element.fields" :key="field.id"
                >
                  <FileUploadElement
                    v-if="field.type === 'input-file'"
                    :element="field"
                    :fileValues="fileValues"
                    :fieldsetId="element.id"
                    :nowTime="nowTime"
                    :dowloadQueue="dowloadQueue"
                    :readonly="isReadonly"
                  />

                  <FieldsetElement
                    v-else-if="field.type ==='fieldset' || field.type==='fieldset-files'"
                    :element="field"
                    :elementValues="elementValues"
                  >
                    <div
                      :class="`section__elements--${field.layout} ${field.layout === 'default' ? '_col(6) _col:md(6) ' : ''} `"
                      v-for="(field2) in field.fields" :key="field2.id"
                    >
                      <FileUploadElement
                        v-if="field2.type === 'input-file'"
                        :element="field2"
                        :fileValues="fileValues"
                        :fieldsetId="field.id"
                        :nowTime="nowTime"
                        :dowloadQueue="dowloadQueue"
                        :readonly="isReadonly"
                      />
                      <SimpleElement
                        v-else
                        :element="field2"
                        :elementValues="elementValues"
                        :readonly="isReadonly"
                        :fieldsetId="field.id"
                        :sectionId="currentSectionId"
                      />
                    </div>
                  </FieldsetElement>
                  <StringWallet
                    v-else-if="field.type === 'string-wallet'"
                    :element="field"
                    :elementValues="elementValues"
                    :readonly="isReadonly"
                    :fieldsetId="element.id"
                    :sectionId="currentSectionId"
                  />
                  <SimpleElement
                    v-else
                    :element="field"
                    :elementValues="elementValues"
                    :readonly="isReadonly"
                    :fieldsetId="element.id"
                    :sectionId="currentSectionId"
                  />
                </div>
              </FieldsetElement>
            </div>
            <div v-else>
              <SimpleElement
                :element="element"
                :elementValues="elementValues"
                :readonly="isReadonly"
                :sectionId="currentSectionId"
              />
            </div>
          </div>
        </div>
        <div class="section__elements-footer _p:x(5) _p:y(3) _m:top(4)">
          <ds ds="button"
            :label="$t('shared.action.saveContinue')"
            version="primary"
            icon="arrow-right"
            type="submit"
            :isDisabled="isReadonly || filesAreLoading"
            style="align-self: flex-end;"
          />
        </div>
      </ds>
      <ds ds="dialog"
        class="_grid"
        ref="updateWarnDialog"
        :isModal="true"
        :isDismissible="true"
      >
        <div class="_m:y(3)">
          <ds
            ds="tag"
            size="md"
            :label="$t('component.SectionForm.updateWarning.message')"
            version="info"
            icon="info"
            iconSize="0.8em"
            class="_p:x(3)"
            isSquare
          />
        </div>

        <div class="_p(4) _m:x(1) _m:top(2)" style="border: 1px solid var(--grey-500);">
          <div class="_flex:column" v-for="(warningLabel) in warningLabelsAndValues" :key="warningLabel.label">
            <div class="_text:sm _m:bottom(2)">
              <span class="_text(500) _m:right(2)">{{ warningLabel.label }}:</span>
              <span>{{ warningLabel.value }} </span>
            </div>
          </div>
        </div>

        <div class="_row _align:right _m:top(4)" >
          <ds ds="button"
            class="_col(8) _col:md(4)"
            type="button"
            icon="arrow-left"
            @click="updateWarningOk = null; $refs.updateWarnDialog.$el.close()"
            :label="$t('shared.action.back')"
            isReverse
          />

          <ds ds="button"
            class="_col(4) _col:md(2)"
            type="button"
            version="primary"
            icon="arrow-right"
            @click="handleUpdateWarning"
            :label="$t('component.SectionForm.updateWarning.action')"
          />
        </div>
      </ds>
      <div v-for="(plugin) in currentSection.plugins" :key="plugin.id" class="section__plugins">
        <PluginContainer :plugin="plugin" :isLocked="isReadonly"/>
      </div>
    </div>
    <div v-if="isReadonly" class="section__locked-overlay"></div>
    <div v-if="isReadonly" class="section__locked-warning">
      <ds
        ds="tag"
        version="warning"
        icon="lock"
        iconSize="1.1rem"
        isSquare
        label="This section is locked, you are viewing your information in read-only mode."
        class="section__locked-tag"
        size="md"
      />
    </div>
    <Loader v-show="isLoading || isSubmitting" />
  </div>
</template>
<script>

import { Loader } from '@/components';
import { LoggerService } from '@/services';
import { eventBus } from '@/eventBus';
import { mapActions, mapGetters } from 'vuex';

const emptyData = () => ({
  isSubmitting: false,
  isValid: false,
  isReadonly: false,
  elements: [],
  elementValues: {},
  elementValuesOriginal: {},
  fileValues: [],
  filesLoading: {},
  noSubmitIds: [],
  dateIds: [],
  nowTime: new Date().getTime(),
  intervalId: null,
  dowloadQueue: [],
  warnOnUpdateEnabled: false,
  updateWarningOk: null,
  warnOnUpdateIds: {},
  warningLabelsAndValues: [],
  pluginCallbacksHandled: []
});

export default {
  name: 'SectionForm',
  data: emptyData,
  props: ['id'],
  components: {
    Loader,
    // https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    SimpleElement: () => import('./SimpleElement.vue'),
    FileUploadElement: () => import('./FileUploadElement.vue'),
    FieldsetElement: () => import('./FieldsetElement.vue'),
    PluginContainer: () => import('./PluginContainer.vue'),
    StringWallet: () => import('./StringWallet.vue')
  },
  computed: {
    ...mapGetters(
      {
        activeMilestone: 'qualification/activeMilestone',
        activeSection: 'qualification/activeSection',
        sections: 'qualification/sections',
        currentSection: 'qualification/currentSection',
        investorPortalUrl: 'qualification/investorPortalUrl'
      }
    ),
    activeSectionIndex () {
      return this.sections.findIndex(s => s === this.activeSection);
    },
    isLoading () {
      return !this.currentSection || this.currentSection.id !== this.id;
    },
    currentSectionHasElements () {
      return this.currentSection && this.currentSection.elements && Array.isArray(this.currentSection.elements) && this.currentSection.elements.length > 0;
    },
    currentSectionHasPlugins () {
      return this.currentSection && this.currentSection.plugins && Array.isArray(this.currentSection.plugins) && this.currentSection.plugins.length > 0;
    },
    filesAreLoading () {
      const keys = Object.keys(this.filesLoading);
      return keys.length > 0 ? keys.reduce((acc, key) => acc && this.filesLoading[key], true) : false;
    },
    currentSectionId () {
      return this.currentSection.id;
    },
    shouldVerifyWallet () {
      const selectedElement = this.currentSection.elements.find(e => e.legend.checkedValue === this.elementValues.existingOrNewWallet);
      if (this.elementValues.select_wallet === 'onchainid') return false;
      if (selectedElement.id === 'select_onchainid_wallet') return false;
      if (selectedElement.id === 'select_wallet') return selectedElement.fields.find(f => f.value === this.elementValues[f.id])?.connectWalletMandatory;
      else return true;
    }
  },
  watch: {
    currentSection: {
      immediate: true, // run on mount
      async handler () {
        if (this.isLoading) return;
        this.resetData();
        try {
          if (this.currentSection.status === 'locked') {
            this.isReadonly = true;
          } else if (this.currentSection.warnOnUpdate) {
            this.warnOnUpdateEnabled = this.currentSection.warnOnUpdate;
          }
          if (!this.currentSectionHasElements && !this.currentSectionHasPlugins) {
            throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
          }
          if (this.currentSectionHasElements) {
            this.elements = this.removeDuplicateIds(this.currentSection.elements);
            // prime keys / values for ds vue wrapper reactivity
            for (const el of this.elements) {
              el.key = el.name || el.id;
              this.mapWarnOnUpdateId(el);

              if (el.type === 'fieldset' || el.type === 'fieldset-grouped') {
                const uniqueFields = this.removeDuplicateIds(el.fields);
                // pull fields out of fieldsets
                for (const f of uniqueFields) {
                  f.key = f.name || f.id;
                  // pull fields out of fieldsets nested in a fieldset (we only support one level deep nesting)
                  if (f.type === 'fieldset') {
                    for (const f2 of f.fields) {
                      f2.key = f2.name || f2.id;
                      this.mapWarnOnUpdateId(f2);
                      this.setFieldValue(f2);
                    }
                  } else {
                    this.mapWarnOnUpdateId(f);
                    this.setFieldValue(f);
                    if (el.type === 'fieldset-grouped') {
                      if (!el.legend || !el.legend.type) {
                        throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
                      } else {
                        this.addNoSubmitId({ id: el.legend.name });
                        this.setFieldValue({ key: el.legend.name, ...el.legend });
                      }
                    }
                  }
                }
              } else if (el.type === 'fieldset-files') {
                // pull file fields out of fieldsets
                this.$set(this.fileValues, el.key, {});
                for (const f of el.fields) {
                  const fieldKey = f.name || f.id;
                  const fileInfo = {
                    id: fieldKey,
                    label: null,
                    filename: f.value && f.value.filename ? f.value.filename : null,
                    type: null,
                    md5: null,
                    size: null,
                    url: f.value && f.value.url ? f.value.url : null,
                    expiryDate: f.value && f.value.expiryDate ? f.value.expiryDate : null,
                    version: f.value && f.value.version ? f.value.version : null
                  };
                  this.$set(this.fileValues[el.key], fieldKey, fileInfo);
                }
              } else if (typeof this.elementValues[el.key] === 'undefined') {
                this.setFieldValue(el);
              }
              if (el.noSubmit) {
                this.addNoSubmitId({ id: el.key });
              }
            }
          }
          if (this.currentSectionHasPlugins) {
            eventBus.$once('pluginCallback', this.handlePluginCallback);
          }
        } catch (error) {
          LoggerService.error(error);
        }
        // update our now timestamp data prop every 5 secs so it can trigger the isExpired computed to refresh
        this.intervalId = setInterval(() => {
          this.nowTime = new Date().getTime();
        }, 1000);
      }
    }
  },
  methods: {
    ...mapActions(
      {
        fetchKyc: 'qualification/fetchKyc',
        fetchQualification: 'qualification/fetchQualification',
        saveSection: 'qualification/saveSection',
        linkWalletToIdentity: 'qualification/linkWalletToIdentity'
      }
    ),
    setFieldValue (el) {
      if (el.type === 'input-date') {
        this.dateIds.push(el.key);
      } else if (el.type === 'input-checkbox') {
        // cast null checkbox values to false
        el.value = el.value ? el.value : false;
      } else if (el.type === 'input-radio') {
        // @todo AG: better way not to overwrite actual radio values with null value from same radio group?
        if (Object.prototype.hasOwnProperty.call(this.elementValues, el.key) && el.value === null) {
          return;
        }
      }
      this.$set(this.elementValues, el.key, el.value);
      this.$set(this.elementValuesOriginal, el.key, el.value);
    },
    addNoSubmitId ({ id }) {
      this.removeNoSubmitId({ id });
      this.noSubmitIds.unshift(id);
    },
    removeNoSubmitId ({ id }) {
      if (this.noSubmitIds.includes(id)) {
        this.noSubmitIds.splice(this.noSubmitIds.indexOf(id), 1);
      }
    },
    async submitSection () {
      if (this.warnOnUpdate() && !this.updateWarningOk) return;
      this.isSubmitting = true;
      const outputValues = this.mapOutputValues(this.elementValues);
      try {
        if (this.currentSectionId === 'wallet' && this.shouldVerifyWallet) {
          const { wallet_signature: signature, select_wallet: existingWallet, create_wallet: walletManual, web3Account } = this.elementValues;
          await this.linkWalletToIdentity({ walletAddress: web3Account || existingWallet || walletManual, signature });
        };

        if (this.valuesModified(outputValues)) {
          await this.saveSection({ sectionId: this.id, elements: outputValues });

          // fetch updated milestones / active section
          await this.fetchQualification();
        }

        // redirect to next active section or back to dashboard if no active section (profile / funnel complete)
        if (!this.activeSection) {
          this.isSubmitting = false;
          await this.fetchKyc();
          // wait 3 secs for User to read id check success message
          setTimeout(() => {
            this.isSubmitting = true;

            if (this.investorPortalUrl) {
              window.location.href = `${this.investorPortalUrl}`;
            } else {
              this.$router.push({ name: 'dashboard' });
            }
          }, 3000);
          return;
        }

        if (!this.activeMilestone) {
          throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
        }

        this.$router.push({ path: `/app/profile/${this.activeMilestone.id}/${this.activeSection.id}` });
      } catch (error) {
        LoggerService.error(error);
      }
      this.isSubmitting = false;
    },
    mapWarnOnUpdateId (el) {
      if (this.warnOnUpdateEnabled && el.standard) {
        const value = el.type === 'input-date' && el.value ? el.value.replace('T00:00:00.000000Z', '') : el.value;
        if (value) {
          this.warnOnUpdateIds[el.key] = { label: el.label, value };
        }
      }
    },
    mapOutputValues (rawInputValues) {
      const outputValues = [];

      // extract web3provider keys / values, set provider if web3InputKey value matches web3Account
      const { web3Account, web3Provider, web3InputKey, ...inputValues } = rawInputValues;

      // create object normal input dto
      for (const id of Object.keys(inputValues)) {
        let value = inputValues[id];
        // filter out noSubmit fields
        if (!this.noSubmitIds.includes(id)) {
          if (id === 'wallet_signature') continue;
          // transform incosistent element type value formats (like birthday being datetime)
          if (this.dateIds.includes(id) && value) {
            if (value.length === 10 && !isNaN(Date.parse(value))) {
              value = `${value}T00:00:00.000000Z`;
            } else {
              throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
            }
          }

          if (id === 'create_wallet' && inputValues.wallet_signature) {
            outputValues.push({ id, value });
          } else if (value !== null) outputValues.push({ id, value });
        }
      }

      return outputValues;
    },
    warnOnUpdate () {
      if (!this.warnOnUpdateEnabled || this.updateWarningOk) return false;

      this.warningLabelsAndValues = [];
      for (const key of Object.keys(this.warnOnUpdateIds)) {
        if (this.elementValues[key] !== this.warnOnUpdateIds[key].value) {
          this.warningLabelsAndValues.push({
            label: this.warnOnUpdateIds[key].label,
            value: this.elementValues[key]
          });
        }
      }

      if (this.warningLabelsAndValues.length) {
        this.updateWarningOk = false;
        this.$refs.updateWarnDialog.$el.open();
        return true;
      }
      return false;
    },
    async handleUpdateWarning () {
      this.updateWarningOk = true;
      await this.submitSection();
    },
    async handlePluginCallback ({ pluginId, eventId, ok, data }) {
      if (!(pluginId in this.pluginCallbacksHandled)) {
        try {
          switch (pluginId) {
            case 'id-check-onfido':
              this.pluginCallbacksHandled.push(pluginId);
              if (!ok) {
                throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
              } else {
                if (eventId === 'done') {
                  await this.submitSection();
                } else if (eventId === 'cancel') {
                  LoggerService.error(this.$t('component.SectionForm.plugins.id-check-onfido.cancel-error'));
                  setTimeout(() => {
                    LoggerService.info(this.$t('component.SectionForm.plugins.id-check-onfido.cancel-info'));
                  }, 1000);
                }
              }
              break;
            case 'id-check-sumsub':
              this.pluginCallbacksHandled.push(pluginId);
              if (!ok) {
                throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
              } else {
                if (eventId === 'done') {
                  await this.submitSection();
                } else if (eventId === 'cancel') {
                  LoggerService.error(this.$t('component.SectionForm.plugins.id-check-sumsub.cancel-error'));
                  setTimeout(() => {
                    LoggerService.info(this.$t('component.SectionForm.plugins.id-check-sumsub.cancel-info'));
                  }, 1000);
                }
              }
              break;
            default:
              throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
          }
        } catch (error) {
          LoggerService.error(error);
        }
      }
    },
    handleFileIsLoading ({ documentId, loading, required }) {
      if (!documentId || required === false) return;
      this.$set(this.filesLoading, documentId, loading);
    },
    resetData () {
      const newData = emptyData();
      for (const key of Object.keys(newData)) {
        this.$set(this, key, newData[key]);
      }
    },
    removeDuplicateIds  (items) { return items.filter((el, i) => items.findIndex(uniqueEl => (el.id === uniqueEl.id)) === i); },
    valuesModified (outputValues) {
      if (this.currentSection.status !== 'complete') return true;
      const modified = outputValues.findIndex(el => el.value !== this.elementValuesOriginal[el.id]) >= 0;
      const hasFiles = this.elements.findIndex(el => el.type === 'fieldset-files' || el.type === 'input-file') >= 0;
      return modified || hasFiles;
    }
  },
  created () {
    eventBus.$on('elementDetached', this.addNoSubmitId);
    eventBus.$on('elementAttached', this.removeNoSubmitId);
    eventBus.$on('fileIsLoading', this.handleFileIsLoading);
  },
  async beforeUnmount () {
    clearInterval(this.intervalId);
  }
};
</script>

<style lang="less" scoped>
  @import '../../utils.less';

  .section {
    position:relative;
  }

  .section__elements {
    display: flex;
    flex-direction: column;
    margin-top: calc(var(--gw) * 1.5);

    &-footer {
      display: flex;
      justify-content: flex-end;
      border-top: 1px solid var(--grey-500);
    }

    legend {
      ._text\:sm;

      padding: calc(var(--gw) * 0.75) calc(var(--gw) * 1.25);
      background-color: var(--grey-400);
    }

    &--default {
      padding-top: calc(var(--gw) * 0.75);
    }

    &--wide {
      padding: calc(var(--gw) * 0.5);
      width: 100%;
    }

    &--toggle {
      padding: 0 calc(var(--gw) * 0.5);
      // max-height: calc(var(--gw) * 1.5);
      width: 100%;
    }
  }

  .section__plugins {
    margin-top: calc(var(--gw) * 1.5);
    margin-bottom: calc(var(--gw) * 1.5);
  }

  .section__locked-warning {
    height: 4rem;
    position: sticky;
    bottom: 0;
    z-index:2999;
  }

  .section__locked-tag {
    width: 100%;
    height: 100%;
    background-color: #f9f0e3;
    color: var(--orange-700);
    padding: 0 2.4rem;
  }

  .section__locked-overlay {
    position: absolute;
    top:0;
    bottom:0;
    left:0;
    right:0;
    background-color: var(--black);
    opacity: 0.1;
    cursor: not-allowed;
    // less than navbar z-index
    z-index:2998;
  }

  .no-wrap {
    white-space: nowrap;
  }
</style>
